import React, { useCallback, useMemo, useEffect, useState } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';
import { getOrCreateGlobalContext, useContextWithValidation } from '@adsk/alloy-react-context';
import { Authorize } from '@adsk/authorize-sdk';

const CONTEXT_NAME = '__TDF_Authorize_Context__';
const TDFAuthorizeContext = getOrCreateGlobalContext<{ isAuthorized: (action: string) => Promise<boolean> }>(
  CONTEXT_NAME
);
const useAuthorized = (action: string, defaultValue: boolean = false) => {
  const { isAuthorized } = useContextWithValidation(TDFAuthorizeContext, {
    hook: 'useAuthorized',
    provider: CONTEXT_NAME
  });
  const [authorized, setAuthorized] = useState(defaultValue);

  useEffect(() => {
    let mounted = true;

    const checker = async () => {
      try {
        const result = await isAuthorized(action);
        if (mounted) setAuthorized(result);
      } catch (err) {
        console.error('[TDF Authorize] Failed to check authorization for action', action, err);
        if (mounted) setAuthorized(defaultValue);
      }
    };
    checker();
    return () => {
      mounted = false;
    };
  }, [action, defaultValue, isAuthorized]);

  return authorized;
};

const TDFAuthorizeProvider = ({ children }: { children: React.ReactNode }) => {
  const project = useCurrentProject();
  const context = useMemo(() => {
    if (!project) return {};
    return {
      accountFeatureKeys: project.accountEntitledFeatureKeys || [],
      userFeatureKeys: project.userEntitledFeatureKeys || [],
      accountId: project.accountId || '',
      projectId: project.id || '',
      accessLevels: project.accessLevels || {}
    };
  }, [project]);
  const instance = useMemo(() => new Authorize(), []);

  const isAuthorized = useCallback(
    async (action: string) => {
      const result = await instance.authorize({
        tdfAuthorization: {
          actions: [action],
          context
        }
      });
      return !!result.tdfAuthorization?.[action]?.allowed;
    },
    [instance, context]
  );

  const value = useMemo(() => ({ isAuthorized }), [isAuthorized]);
  return <TDFAuthorizeContext.Provider value={value}>{children}</TDFAuthorizeContext.Provider>;
};

export { useAuthorized, TDFAuthorizeProvider };
